import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell, { SectionCard } from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  CheckCircle, Warning, XCircle, Play, Gear, DownloadSimple,
  ArrowSquareOut, LockSimple, ArrowClockwise,
} from "@phosphor-icons/react";
import { fmtISTDate } from "../lib/utils";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const currentYM = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const STATUS_LABEL = {
  draft: "Draft",
  computing: "Computing…",
  computed: "Ready to review",
  finalised: "Finalised · Ready to publish",
  published: "Published to employees",
};
const STATUS_COLOR = {
  draft: "bg-zinc-100 text-zinc-700",
  computing: "bg-amber-100 text-amber-800",
  computed: "bg-blue-100 text-blue-800",
  finalised: "bg-purple-100 text-purple-800",
  published: "bg-emerald-100 text-emerald-800",
};

/**
 * PayrollRun — simplified monthly page.
 * Shows a readiness card, a one-click "Run payroll" action, and a compact
 * history strip. No tabs, no wizard, no state-machine buttons.
 */
export default function PayrollRun() {
  const [month, setMonth] = useState(currentYM());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const nav = useNavigate();

  const load = async (ym = month) => {
    setBusy(true);
    try {
      const r = await api.get(`/payroll/month/${ym}`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };
  useEffect(() => { load(month); /* eslint-disable-line */ }, [month]);

  const runNow = async (excludeMissing = false) => {
    const msg = excludeMissing
      ? `Run payroll for ${data?.preflight?.period_month}, leaving out employees without a CTC?`
      : `Run payroll for ${data?.preflight?.period_month}?\nThis will compute payslips for all active employees.`;
    if (!window.confirm(msg)) return;
    setRunning(true);
    try {
      const r = await api.post(`/payroll/run-month/${month}${excludeMissing ? "?exclude_missing_ctc=true" : ""}`);
      toast.success(`Payroll ready — ${r.data.run.payslip_count} payslips computed.`);
      load(month);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Payroll run failed"));
    } finally { setRunning(false); }
  };

  const publish = async () => {
    if (!data?.run) return;
    if (!window.confirm("Publish payslips to employees? This sends emails and locks the run. Cannot be undone by admins.")) return;
    try {
      await api.post(`/payroll-runs/${data.run.id}/publish`);
      toast.success("Payslips published to employees.");
      load(month);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const undo = async () => {
    if (!window.confirm("Undo and re-run? This deletes the current draft payslips.")) return;
    try {
      await api.post(`/payroll/undo-month/${month}`);
      toast.success("Draft cleared. You can Run payroll again.");
      load(month);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const pf = data?.preflight;
  const run = data?.run;
  const history = data?.history || [];
  const isLocked = run && ["finalised", "published"].includes(run.status);

  return (
    <AppShell title="Payroll" moduleKey="payroll">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <input
            type="month"
            className="border border-zinc-300 rounded h-9 px-2 text-sm font-medium"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            data-testid="pay-month"
          />
          <span className="text-xs text-zinc-500">Change month to view another period</span>
        </div>
        <Button size="sm" variant="outline" onClick={() => nav("/app/payroll/setup")}
          data-testid="goto-setup">
          <Gear size={14}/> One-time setup
        </Button>
      </div>

      {busy && !data ? (
        <div className="text-sm text-zinc-500 p-6">Loading…</div>
      ) : !pf ? (
        <div className="text-sm text-zinc-500 p-6">No data.</div>
      ) : (
        <div className="grid gap-4">
          {/* ── Readiness card ── */}
          <SectionCard
            title={`${new Date(month + "-01").toLocaleString("en-IN", { month: "long", year: "numeric" })} payroll`}
            subtitle={`${pf.active_employees} employees · ${pf.working_days} working days · ${pf.holidays_in_month} holidays`}
            testid="readiness-card"
          >
            <div className="grid gap-2">
              <ReadyRow ok={pf.blockers.every(b => b.code !== "no_employees" && b.code !== "missing_ctc") && pf.with_ctc === pf.active_employees}
                label="Employee compensations"
                detail={`${pf.with_ctc} / ${pf.active_employees} have CTC assigned`}
                cta={pf.with_ctc < pf.active_employees ? { label: "Fix in Setup", to: "/app/payroll/setup" } : null} />
              <ReadyRow ok={pf.warnings.every(w => w.code !== "biometric_not_imported")}
                label="Attendance imported"
                detail={pf.biometric_employees ? `${pf.biometric_imports_this_month} biometric import(s) for this month · ${pf.biometric_employees} employees track via biometric` : "No biometric-tracked employees"}
                cta={pf.warnings.some(w => w.code === "biometric_not_imported") ? { label: "Import file", to: "/app/attendance/biometric-import" } : null} />
              <ReadyRow ok={pf.pending_overtime === 0}
                warn={pf.pending_overtime > 0}
                label="Overtime approvals"
                detail={pf.pending_overtime ? `${pf.pending_overtime} OT rows pending HR approval — they won't be paid until approved` : "All OT approved (or none)"}
                cta={pf.pending_overtime > 0 ? { label: "Approve OT", to: "/app/overtime-approvals" } : null} />
              <ReadyRow ok={pf.warnings.every(w => w.code !== "pending_leaves")}
                warn={pf.warnings.some(w => w.code === "pending_leaves")}
                label="Leave applications"
                detail={pf.warnings.find(w => w.code === "pending_leaves")?.message || "No overlapping pending leaves"}
                cta={pf.warnings.some(w => w.code === "pending_leaves") ? { label: "Review leaves", to: "/app/leave-admin" } : null} />
              <ReadyRow ok={pf.statutory_ok}
                label="Statutory config"
                detail={pf.statutory_ok ? "PF · ESIC · PT · LWF · TDS rules loaded" : "Company state & LWF rules pending — run Auto-configure in Setup"}
                cta={{ label: pf.statutory_ok ? "Edit" : "Fix in Setup", to: "/app/payroll/setup" }} />
            </div>

            {pf.blockers.length > 0 && (
              <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 p-3 space-y-2" data-testid="payroll-blockers">
                {pf.blockers.map(b => (
                  <div key={b.code} className="text-sm text-rose-800" data-testid={`blocker-${b.code}`}>
                    <div className="font-medium">{b.message}</div>
                    {b.sample?.length > 0 && (
                      <div className="text-xs text-rose-700 mt-0.5">
                        {b.sample.map(x => `${x.name}${x.code ? ` (${x.code})` : ""}`).join(", ")}{b.count > b.sample.length ? ` +${b.count - b.sample.length} more` : ""}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {run?.errors?.length > 0 && (
              <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800" data-testid="run-errors">
                <div className="font-medium">{run.errors.length} payslips have negative net pay — finalise is blocked.</div>
                <div className="text-xs mt-0.5">{run.errors.slice(0, 5).map(e => `${e.employee_name} (${inr(e.net)})`).join(", ")}</div>
              </div>
            )}

            <AttendanceSheetCard month={month} locked={run && ["finalised", "published"].includes(run.status)} />

            {/* ── Big action row ── */}
            <div className="mt-5 pt-4 border-t border-zinc-200">
              {!run && (
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm text-zinc-600">
                    {pf.can_compute
                      ? <>Ready to run payroll for <b>{pf.active_employees}</b> employees.</>
                      : <>Fix the blockers above before running payroll.</>}
                  </div>
                  {!pf.can_compute && pf.blockers.length > 0 && pf.blockers.every(b => b.code === "missing_ctc") && (
                    <Button variant="outline" onClick={() => runNow(true)} disabled={running} data-testid="run-payroll-exclude-btn">
                      Run without them
                    </Button>
                  )}
                  <Button
                    size="lg"
                    onClick={() => runNow(false)}
                    disabled={!pf.can_compute || running}
                    data-testid="run-payroll-btn"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                  >
                    <Play size={18} weight="fill"/> {running ? "Running…" : "Run payroll"}
                  </Button>
                </div>
              )}
              {run && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="text-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={`text-[11px] ${STATUS_COLOR[run.status]}`}>
                        {STATUS_LABEL[run.status] || run.status}
                      </Badge>
                      <span className="text-zinc-500">·</span>
                      <span>{run.payslip_count || 0} payslips</span>
                      {run.total_gross && (<><span className="text-zinc-500">·</span>
                        <span className="font-mono">{inr(run.total_gross)} gross</span></>)}
                    </div>
                    <div className="text-xs text-zinc-500">
                      Last computed {run.updated_at?.slice(0,16).replace("T"," ")} IST
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => nav(`/app/payroll-runs?run=${run.id}`)}
                      data-testid="review-payslips-btn">
                      Review payslips <ArrowSquareOut size={12}/>
                    </Button>
                    {run.status === "finalised" && (
                      <Button size="sm" onClick={publish}
                        data-testid="publish-btn"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5">
                        <CheckCircle size={14}/> Publish to employees
                      </Button>
                    )}
                    {run.status === "published" && (
                      <div className="text-xs text-emerald-700 flex items-center gap-1">
                        <LockSimple size={12}/> Published & locked
                      </div>
                    )}
                    {!isLocked && (
                      <Button size="sm" variant="outline" onClick={runNow} disabled={running} data-testid="rerun-btn">
                        <ArrowClockwise size={14}/> Recompute
                      </Button>
                    )}
                    {run.status === "finalised" && (
                      <Button size="sm" variant="ghost" onClick={undo} data-testid="undo-btn"
                        className="text-rose-600 hover:text-rose-700">
                        Undo & re-run
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </SectionCard>

          {/* ── History strip ── */}
          {history.length > 0 && (
            <SectionCard title="Past runs" subtitle={`Last ${history.length} months`}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-[11px] uppercase tracking-wider text-zinc-500">
                    <tr>
                      <th className="text-left py-2 px-3 font-medium">Month</th>
                      <th className="text-left py-2 px-3 font-medium">Status</th>
                      <th className="text-right py-2 px-3 font-medium">Payslips</th>
                      <th className="text-right py-2 px-3 font-medium">Gross</th>
                      <th className="text-right py-2 px-3 font-medium">Net paid</th>
                      <th className="text-right py-2 px-3 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map(h => (
                      <tr key={h.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                        <td className="py-2 px-3 font-medium">{h.period_label}</td>
                        <td className="py-2 px-3">
                          <Badge className={`text-[10px] ${STATUS_COLOR[h.status]}`}>{h.status}</Badge>
                        </td>
                        <td className="py-2 px-3 text-right font-mono">{h.payslip_count || 0}</td>
                        <td className="py-2 px-3 text-right font-mono">{inr(h.total_gross)}</td>
                        <td className="py-2 px-3 text-right font-mono">{inr(h.total_net)}</td>
                        <td className="py-2 px-3 text-right">
                          <Button size="sm" variant="ghost" onClick={() => setMonth(h.period_month)}
                            data-testid={`view-${h.period_month}`}>Open</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          )}
        </div>
      )}
    </AppShell>
  );
}

function ReadyRow({ ok, warn, label, detail, cta }) {
  const Icon = ok ? CheckCircle : (warn ? Warning : XCircle);
  const tone = ok ? "text-emerald-600" : (warn ? "text-amber-600" : "text-rose-600");
  const nav = useNavigate();
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-zinc-100 last:border-b-0">
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <Icon size={18} weight={ok ? "fill" : "bold"} className={`${tone} shrink-0 mt-0.5`}/>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">{label}</div>
          <div className="text-xs text-zinc-500 mt-0.5">{detail}</div>
        </div>
      </div>
      {cta && (
        <Button size="sm" variant="outline" className="shrink-0"
          onClick={() => nav(cta.to)}
          data-testid={`ready-cta-${label.toLowerCase().replace(/\s+/g,"-")}`}>
          {cta.label}
        </Button>
      )}
    </div>
  );
}


function AttendanceSheetCard({ month, locked }) {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/payroll/attendance-summary/${month}`).then(r => setInfo(r.data)).catch(() => setInfo(null));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/payroll/attendance-summary/${month}/import`, fd);
      toast.success(`Attendance sheet loaded for ${r.data.matched} employees${r.data.unmatched_count ? ` · ${r.data.unmatched_count} codes not found` : ""}`);
      load();
    } catch { /* global toast */ } finally { setBusy(false); }
  };
  const clear = async () => {
    if (!window.confirm("Remove the uploaded attendance sheet? Payroll will go back to device attendance.")) return;
    await api.delete(`/payroll/attendance-summary/${month}`); load();
  };
  return (
    <div className="mt-4 rounded-md border border-zinc-200 p-3 flex flex-wrap items-center justify-between gap-3" data-testid="attendance-sheet-card">
      <div className="text-sm">
        <div className="font-medium">Monthly attendance sheet <span className="text-zinc-400 font-normal">(optional)</span></div>
        <div className="text-xs text-zinc-500">
          {info?.count
            ? <>Using <b>{info.source_file}</b> for {info.count} employees — absent days, Sundays, nights and OT come from this sheet instead of the biometric device.</>
            : <>Upload your register (Employee ID, Absent, Sundays Worked, Night Present, OT Hours) to override device attendance for this month. Punch-sheet companies can instead send <b>Employee ID + Adjustment</b> (± payable days), <b>Total Payable Days</b> and <b>Night Present</b>.</>}
        </div>
      </div>
      {!locked && (
        <div className="flex gap-2">
          {info?.count > 0 && <Button size="sm" variant="ghost" onClick={clear} data-testid="attendance-sheet-clear">Remove</Button>}
          <label className="inline-flex">
            <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={e => upload(e.target.files?.[0])} data-testid="attendance-sheet-input"/>
            <span className="cursor-pointer inline-flex items-center h-8 px-3 rounded-md border border-zinc-300 text-sm hover:bg-zinc-50" data-testid="attendance-sheet-upload">
              {busy ? "Uploading…" : info?.count ? "Replace sheet" : "Upload sheet"}
            </span>
          </label>
        </div>
      )}
    </div>
  );
}
